import React, {createContext, useContext, useState, ReactNode, useRef, useEffect, ElementType} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {
    Box,
    HStack,
    Text,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    useMultiStyleConfig,
    FlexProps,
    TextProps,
    StackProps,
    SlideOptions,
} from '@chakra-ui/react'
import {IconButton, Icon} from '_uiComponents'
import {IconNameTypes} from '../../types'
import {useViewport} from '_customHooks'

type LogicalPlacement = 'start' | 'end'

type DrawerPlacement = SlideOptions['direction'] | LogicalPlacement

// Sizes allowed for the header close (X) / back (<) icon. 'sm-plus' (18px) matches the Figma spec
// for the PDP modals/drawers (node 5655-4320).
type ClosingIconSize = 'sm' | 'sm-plus' | 'md' | 'lg'

interface DrawerOptions {
    title?: string | ReactNode
    titleIcon?: IconNameTypes
    body: ReactNode
    footer?: ReactNode
    size?: 'md' | 'lg'
    variant?: string
    motionProps?: Record<string, unknown>
    autoClosingTimeout?: number
    closingIcon?: IconNameTypes
    closingIconSize?: ClosingIconSize
    placement?: DrawerPlacement
    onOpen?: () => void
    onClose?: (...params) => void
    onBack?: () => void
    titleAs?: ElementType
}

interface DrawerContextType {
    openDrawer: (options: DrawerOptions) => void
    closeDrawer: () => void
    updateDrawerTitle: (title: string | ReactNode) => void
    updateDrawerFooter: (footer: ReactNode) => void
    updateDrawerBody: (body: ReactNode) => void
    updateClosingIcon: (icon: IconNameTypes, size: ClosingIconSize) => void
    isOpen: boolean
    titleAs?: ElementType
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined)

export const DrawerProvider = React.memo(({children}: {children: ReactNode}) => {
    const styles = useMultiStyleConfig('Drawer') as Record<string, FlexProps & TextProps & StackProps>
    const {formatMessage} = useIntl()
    const {isMobile} = useViewport()
    const location = useLocation()

    const drawerRef = useRef<HTMLDivElement>(null)
    const drawerContentRef = useRef<HTMLDivElement>(null)
    // Target for the Drawer's initialFocusRef so focus lands on the heading (a11y best practice) rather
    // than the first tabbable control (see the initialFocusRef comment on <Drawer>).
    const drawerTitleRef = useRef<HTMLParagraphElement>(null)
    const drawerTitleId = 'drawer-title'

    const [isOpen, setIsOpen] = useState(false)
    const [variant, setVariant] = useState<string | undefined>()
    const [drawerTitle, setDrawerTitle] = useState<string | ReactNode | undefined>()
    const [drawerTitleIcon, setDrawerTitleIcon] = useState<IconNameTypes | undefined>()
    const [drawerBody, setDrawerBody] = useState<ReactNode | null>(null)
    const [drawerFooter, setDrawerFooter] = useState<ReactNode | null>(null)
    const [drawerSize, setDrawerSize] = useState<string>('lg')
    const [closingIcon, setClosingIcon] = useState<IconNameTypes>('IconClose')
    const [closingIconSize, setClosingIconSize] = useState<ClosingIconSize>('md')
    const [drawerMotionProps, setDrawerMotionProps] = useState<DrawerOptions['motionProps']>(undefined)
    const [closingTimeoutId, setClosingTimeoutId] = useState<number | undefined>()
    const [drawerPlacement, setDrawerPlacement] = useState<DrawerPlacement>()
    const onOpenCallbackRef = useRef<(() => void) | undefined>()
    const onCloseCallbackRef = useRef<(() => void) | undefined>()
    const [onBackCallback, setOnBackCallback] = useState<(() => void) | undefined>()
    const [titleAs, setTitleAs] = useState<DrawerOptions['titleAs']>('p')

    // Auto-close timer handle. Kept in a ref (not state) so that cancelling it on user interaction does
    // NOT re-render the provider subtree. Previously this was a `shouldStopTimer` state flag toggled by
    // DrawerContent's onMouseEnter; on iOS Safari the first tap fires an *emulated* mouseenter, and the
    // resulting state change re-rendered the whole drawer on that same tap — WebKit then withheld the
    // first `click`, so the footer "go to the cart" CTA needed a second tap. A ref-based cancel removes
    // that re-render, so the first tap activates the link. (Desktop `data-no-focus-lock`/`initialFocusRef`
    // fixes handled the mouse focus-lock case; this handles the touch case.)
    const closingTimerRef = useRef<number | undefined>()

    const openDrawer = ({
        title,
        titleIcon,
        body,
        footer,
        size = 'lg',
        variant,
        motionProps,
        onOpen,
        onClose,
        onBack,
        autoClosingTimeout,
        placement,
        closingIcon = 'IconClose',
        closingIconSize = 'md' /*, onBack */,
        titleAs = 'p',
    }: DrawerOptions) => {
        setDrawerTitle(title)
        setDrawerTitleIcon(titleIcon)
        setDrawerBody(body)
        setDrawerFooter(footer)
        setDrawerSize(size)
        onOpenCallbackRef.current = onOpen
        onCloseCallbackRef.current = onClose
        setOnBackCallback(() => onBack)
        setVariant(variant)
        setClosingTimeoutId(autoClosingTimeout)
        setDrawerPlacement(placement ? placement : isMobile ? 'bottom' : 'right')
        setIsOpen(true)
        setClosingIcon(closingIcon)
        setClosingIconSize(closingIconSize)
        setDrawerMotionProps(motionProps)
        setTitleAs(titleAs)
    }

    const closeDrawer = () => {
        if (onCloseCallbackRef.current) onCloseCallbackRef.current()
        setIsOpen(false)
    }

    /**
     * DrawerProvider is mounted once in the app shell (see _app/index.tsx), ABOVE the router, so it never
     * unmounts on client-side navigation - only the routed page below it swaps out. A `Link` (or any other
     * react-router navigation) rendered inside the drawer body therefore does NOT close the drawer:
     * nothing calls closeDrawer(), so the drawer (and any cleanup its onClose performs, e.g. restoring
     * `document.body.style.overflow`) keeps floating on top of the newly navigated page.
     *
     * We only react to a PATHNAME change (a real navigation to a different page). Some in-page flows
     * update just the query string via `history.replace` while intentionally keeping a drawer open, so
     * those must not trigger a close here.
     */
    const pathnameRef = useRef(location.pathname)
    useEffect(() => {
        if (isOpen && location.pathname !== pathnameRef.current) {
            closeDrawer()
        }
        pathnameRef.current = location.pathname
    }, [location.pathname])

    const updateDrawerTitle = (title: string | ReactNode) => {
        setDrawerTitle(title)
    }

    const updateDrawerBody = (body: ReactNode) => {
        setDrawerBody(body)
    }

    const updateDrawerFooter = (footer: ReactNode) => {
        setDrawerFooter(footer)
    }

    const updateClosingIcon = (icon: IconNameTypes, size: ClosingIconSize) => {
        setClosingIcon(icon)
        setClosingIconSize(size)
    }

    // Cancel the auto-close countdown without triggering a re-render (see closingTimerRef above). Called
    // on the first user interaction with the drawer (pointer enter / touch), so the preview does not close
    // out from under the user. Idempotent and safe to call on every event.
    const stopClosingTimer = () => {
        if (closingTimerRef.current !== undefined) {
            clearTimeout(closingTimerRef.current)
            closingTimerRef.current = undefined
        }
    }

    useEffect(() => {
        if (!closingTimeoutId) return

        closingTimerRef.current = window.setTimeout(() => {
            closeDrawer()
        }, closingTimeoutId)

        return () => {
            clearTimeout(closingTimerRef.current)
            closingTimerRef.current = undefined
        }
    }, [closingTimeoutId])

    /**
     * Since Chakra does not support setup of `aria-labelledby` parameter on drawer content
     * this setup on opening ensures that it is configured correctly pointing to the text that
     * actually contains the title label of the Drawer.
     */
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (drawerContentRef.current) {
                    drawerContentRef.current.setAttribute('aria-labelledby', drawerTitleId)
                }
            }, 500)

            if (onOpenCallbackRef.current) onOpenCallbackRef.current()
        }
        // `openDrawer` is recreated on every render; only `isOpen` toggling should retrigger this effect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    return (
        <DrawerContext.Provider value={{openDrawer, closeDrawer, updateDrawerTitle, updateDrawerBody, updateDrawerFooter, updateClosingIcon, isOpen, titleAs}}>
            {children}
            <Drawer
                isOpen={isOpen}
                onClose={closeDrawer}
                size={drawerSize}
                variant={variant}
                placement={drawerPlacement}
                returnFocusOnClose={variant === 'shopTheImage' ? false : undefined}
                autoFocus={variant === 'shopTheImage' ? false : undefined}
                // Send initial focus to the drawer heading instead of letting react-focus-lock pick the first
                // tabbable element itself. This satisfies the modal-dialog focus rules (focus moves into the
                // dialog on open — WCAG 2.4.3 — and lands on the heading, the accessibility best practice)
                // while the trap and returnFocusOnClose stay active. It also fixes a Safari-only double-click
                // on the footer "go to the cart" link when the minicart auto-opens as a preview after an
                // add-to-cart from a product tile overlay (e.g. Einstein/recommendation): the add-to-cart
                // button that owned focus is unmounted in the same tick the drawer opens, so on WebKit the
                // focus-lock was left re-resolving which element to focus and consumed the first click on the
                // cart link. Focusing a deterministic, non-interactive target (the heading) on open settles
                // focus up front, so the first click lands. The header title gets tabIndex={-1} so it is
                // programmatically focusable without entering the tab order.
                initialFocusRef={variant !== 'shopTheImage' && drawerTitle ? drawerTitleRef : undefined}
            >
                <DrawerOverlay />
                {/* Stop the auto-close countdown on first interaction. onPointerEnter covers mouse; onTouchStart
                    covers touch (mobile Safari never fires a real mouseenter). stopClosingTimer only clears a
                    ref'd timeout — it does NOT set state — so the first tap does not re-render the subtree,
                    which is what previously made WebKit withhold the first click on the footer CTA. */}
                <DrawerContent
                    ref={drawerContentRef}
                    onPointerEnter={stopClosingTimer}
                    onTouchStart={stopClosingTimer}
                    motionProps={drawerMotionProps}
                >
                    <DrawerHeader>
                        {drawerTitle && (
                            <HStack {...styles.titleRow}>
                                {drawerTitleIcon && (
                                    <Box mr={drawerSize === 'md' ? 'spacing.4' : '0'}>
                                        <Icon aria-hidden={true} name={drawerTitleIcon} size={drawerSize === 'md' ? 'sm' : 'lg'} />
                                    </Box>
                                )}
                                {/* tabIndex={-1} makes the heading programmatically focusable (initialFocusRef
                                    target) without adding it to the tab order. The heading receives focus on
                                    open programmatically (not via mouse/keyboard), so `:focus-visible` won't
                                    fire and no ring shows for that; the explicit non-focus-visible reset keeps
                                    it clean if a browser still paints a plain `:focus` outline, while keyboard
                                    focus (`:focus-visible`) keeps its visible indicator. */}
                                <Text
                                    ref={drawerTitleRef}
                                    as={titleAs}
                                    id={drawerTitleId}
                                    tabIndex={-1}
                                    sx={{'&:focus:not(:focus-visible)': {outline: 'none'}}}
                                    {...styles.headerTitle}
                                >
                                    {drawerTitle}
                                </Text>
                            </HStack>
                        )}
                        <Box {...styles.closeIcon}>
                            <IconButton
                                size="iconButtonSm"
                                icon={<Icon name={closingIcon} size={closingIconSize} />}
                                onClick={closingIcon === 'IconChevronLeft' ? onBackCallback : closeDrawer}
                                aria-label={formatMessage({id: 'alt.link.close', defaultMessage: 'Close'})}
                            />
                        </Box>
                    </DrawerHeader>

                    <DrawerBody ref={drawerRef}>{drawerBody}</DrawerBody>

                    {drawerFooter && <DrawerFooter>{drawerFooter}</DrawerFooter>}
                </DrawerContent>
            </Drawer>
        </DrawerContext.Provider>
    )
})

DrawerProvider.displayName = 'DrawerProvider'

export const useDrawer = (): DrawerContextType => {
    const context = useContext(DrawerContext)
    if (!context) {
        throw new Error('useDrawer must be used within a DrawerProvider')
    }
    return context
}
